/**
 * AddToCartFeedbackContext
 *
 * Global context for managing the add-to-cart feedback panel.
 * Allows the feedback to be triggered from anywhere in the app.
 */

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { ShopifyProduct, ShopifyProductVariant } from '../types/shopify';
import AddToCartFeedback from '../components/cart/AddToCartFeedback';

interface FeedbackState {
  product: ShopifyProduct | null;
  variant: ShopifyProductVariant | null;
  quantity: number;
}

interface AddToCartFeedbackContextType {
  showFeedback: (product: ShopifyProduct, variant: ShopifyProductVariant, quantity: number) => void;
  hideFeedback: () => void;
}

const AddToCartFeedbackContext = createContext<AddToCartFeedbackContextType | undefined>(undefined);

export const AddToCartFeedbackProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [feedbackState, setFeedbackState] = useState<FeedbackState>({
    product: null,
    variant: null,
    quantity: 0,
  });
  const [triggerCount, setTriggerCount] = useState(0);

  const showFeedback = useCallback((product: ShopifyProduct, variant: ShopifyProductVariant, quantity: number) => {
    setFeedbackState({ product, variant, quantity });
    setTriggerCount(c => c + 1); // Force timer reset even for same item
    setIsOpen(true);
  }, []);

  const hideFeedback = useCallback(() => {
    setIsOpen(false);
  }, []);

  return (
    <AddToCartFeedbackContext.Provider value={{ showFeedback, hideFeedback }}>
      {children}
      <AddToCartFeedback
        isOpen={isOpen}
        onClose={hideFeedback}
        product={feedbackState.product}
        variant={feedbackState.variant}
        quantity={feedbackState.quantity}
        triggerCount={triggerCount}
      />
    </AddToCartFeedbackContext.Provider>
  );
};

export const useAddToCartFeedback = (): AddToCartFeedbackContextType => {
  const context = useContext(AddToCartFeedbackContext);
  if (context === undefined) {
    throw new Error('useAddToCartFeedback must be used within an AddToCartFeedbackProvider');
  }
  return context;
};
